//© all rights reserved to 6over6 Ltd. 2022. This software (the "Software") is the property of 6over6 Ltd. and its licensors. Any use of the Software is strictly prohibited unless an explicit license or permission is granted by 6over6 Ltd.
import {
  SetAppStyles,
  SetInitSessionDetails,
  updateHttpClient,
  mountApp,
} from "./ApiService.js";
import HttpClient from "@/../../../common/services/HttpClient.js";
import Utils from "@/../../../common/services/Utils.js";
import Store from "@/store";
import ErrorLogger from "@/../../../common/services/ErrorLogger.js";
import AnalyticsLogger from "@/../../../common/services/AnalyticsLogger.js";
import NoSleep from "nosleep.js";
import CallbackEnums from "@/../../../common/enums/CallbackEnums.js";
import MockService from "@/services/mock/MockService.js";
import PhoneUtil from "google-libphonenumber";
import ActionsManager from "@/../../../common/services/ActionsManager.js";
import CallbackService from "@/../../../common/services/CallbackService.js";
const phoneUtil = PhoneUtil.PhoneNumberUtil.getInstance();

class Api {
  constructor() {
    this.appVersion = require("../package.json").version;
    // this.appConfig = App;
    this.initCompleted = false;
    this.shouldOpen = false;

    this.options = {
      containerId: "glasseson",
      showResultPage: false,
      trackEvents: true,
    };

    this.logger = null;
    this.analytics = null;
    this.mockService = null;
    this.noSleep = new NoSleep();
    this.callbackService = CallbackService;
    this.callbackService.setStore(Store);
  }

  async init(clientId, serverUrl = "https://www.glasseson.com", options = {}) {
    if (this.initCompleted) {
      console.log("Glasseson has already initialized");
      return;
    }
    this.store = Store; //TODO remove for debug
    this.options = { ...this.options, ...options };
    SetAppStyles(this.options);
    SetInitSessionDetails({
      clientId,
      serverUrl,
      options,
      version: this.appVersion,
    });

    const headerDetails = [
      {
        header: "GlassesOn-Client-Id",
        value: clientId,
      },
      { header: "GlassesOn-Profile-Name", value: this.options.profileName },
    ];
    updateHttpClient(serverUrl, headerDetails);

    try {
      const response = await HttpClient.get("/@/webcompanion/init", {
        params: {
          is_desktop: Utils.isMobile() ? "false" : true,
        },
      });
      Store.state.Session.campaign = response.data.campaign_properties.id;
      this.initCompleted = true;
      this.config = {
        clientConfig: {
          clientId,
          serverUrl,
        },
        appConfig: response.data,
      };
      const productTag = "WEBCOMPANION";
      ErrorLogger.init(this.config, `${productTag}_GENERAL`, Store);
      AnalyticsLogger.init(
        this.config,
        Store,
        productTag,
        this.options.trackEvents
      );
      this.logger = ErrorLogger;
      this.analytics = AnalyticsLogger;

      if (this.shouldOpen) {
        console.log("Glasseson.open has already been called");
        this.open();
      }
    } catch (err) {
      console.log("init error: " + err);
      return Promise.reject(err);
    }
  }

  async open() {
    if (!this.initCompleted) {
      this.shouldOpen = true;
      console.log("Glasseson not initialized waiting...");
      return;
    }

    if (this.app) {
      console.log("Glasseson already open");
      return;
    }
    console.log("opening app");
    this.app = await mountApp(this);
    this.shouldOpen = false;
  }

  close() {
    this.app.unmount();
    Store.state.showResultsPage = false;
    this.app = null;
  }

  async showResultsPage({
    results = Store.state.Results,
    buttonsInfo = [],
  } = {}) {
    if (!this.app) {
      await this.open();
    }
    Store.state.showResultsPage = true;
    Store.state.resultsPageInfo = {
      btns: buttonsInfo,
    };
    Store.dispatch("Results/setResults", results);
  }

  async getPrescription() {
    try {
      const params = new URLSearchParams(window.location.search);
      let data = params.get("data");
      let res = await HttpClient.post("/go-eyes/get-prescription", { data });
      Store.dispatch("Results/setPrescription", res.data);
      return Store.state.Results;
    } catch (e) {
      console.error("failed to extract prescription with error: " + e);
      return {};
    }
  }

  getUserId() {
    return Store.state.Session.userId;
  }

  getSessionId() {
    return Store.state.Session.sessionId;
  }

  setAnalyticsCallback(callback) {
    this.callbackService.setCallback(CallbackEnums.ANALYTICS, callback);
  }

  setCloseCallback(callback) {
    this.callbackService.setCallback(CallbackEnums.CLOSE, callback);
  }

  setResultCallback(callback) {
    this.callbackService.setCallback(CallbackEnums.RESULTS, callback);
  }

  setCloseResultsPageCallback(callback) {
    this.callbackService.setCallback(CallbackEnums.CLOSERESULTSPAGE, callback);
  }

  setCustomerParametersCallback(callback) {
    this.callbackService.setCallback(
      CallbackEnums.CUSTOMERPARAMETERS,
      callback
    );
  }

  async sendSms(phoneNumber, extraData = {}) {
    let status = {
      success: false,
      errorType: null,
    };
    if (!phoneNumber) {
      status.errorType = "INVALID-PHONE-NUMBER";
      return status;
    }

    const number = phoneUtil.parseAndKeepRawInput(phoneNumber);
    if (!phoneUtil.isValidNumber(number)) {
      status.errorType = "INVALID-PHONE-NUMBER";
      return status;
    }

    try {
      await HttpClient.post("/sms/send", { phoneNumber, ...extraData });
      status.success = true;
      return status;
    } catch (err) {
      status.errorType = "INVALID";
      return status;
    }
  }

  autoMockMode(
    delay = 4000,
    sessionMockId = "auto-mock",
    userMockId = "user-mock"
  ) {
    this.startMockMode(sessionMockId, userMockId);
    let intervalId = null;
    const intervalFn = async () => {
      const stageData = await this.moveToNextMockStage();
      if (!stageData) {
        clearInterval(intervalId);
        intervalId = null;
        return;
      }
    };
    intervalId = setInterval(intervalFn, delay);
  }

  startMockMode(sessionId, userId) {
    Store.state.isMockRun = true;
    Store.state.Session.sessionId = sessionId;
    Store.state.Session.userId = userId;
    Store.state.Results.id = `${userId}/${sessionId}`;
    // this.http.defaults.headers.common["GlassesOn-Session-Id"] = sessionId;

    this.mockService = MockService;
    console.log(
      "Entered mock mode. Call 'moveToNextMockStage' in order to start."
    );
  }

  async moveToNextMockStage() {
    const specificActions = {
      onQRScanned: () => {
        Store.state.customerParameters = [];
      },
      onScreenCalibrated: () => {
        Store.dispatch("Resolution/setHasScreenCalibrated", true);
      },
      onResultsReady: (stageData) => {
        Store.dispatch("Results/setPrescription", stageData.result);
      },
      onPdReady: (stageData) => {
        Store.dispatch("Results/setPrescription", stageData.result);
      },
    };
    let stageData = this.mockService.getNextStage();
    if (stageData != null) {
      if (stageData.stage in specificActions) {
        specificActions[stageData.stage](stageData);
      }
      if (!this.actionsManager) {
        this.actionsManager = new ActionsManager(Store);
      }
      await this.actionsManager.addActions(stageData.actions);
      return stageData;
    }
  }
}

export default Api;
